import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Box,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { navLinks } from "../Constants/NavLinks";
import { socialLinks } from "../Constants/NavLinks";
import CloseIcon from "@mui/icons-material/Close";
import { Link } from "react-router-dom";

const Navbar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("1200"));

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [submenuItems, setSubmenuItems] = useState([]);

  const [isTop, setIsTop] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      setIsTop(window.scrollY <= window.innerHeight / 1.1);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleMenuOpen = (event, items) => {
    setAnchorEl(event.currentTarget);
    setSubmenuItems(items);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSubmenuItems([]);
  };

  const renderNavLinks = () => (
    <Box
      sx={{
        display: "flex",
        pr: 1,
        mr: 1,
        borderRight: isTop
          ? "2px solid rgba(255, 255, 255, 1)"
          : "2px solid rgba(33, 93, 213, 1)",
      }}
    >
      {navLinks.map((link) =>
        link.submenu ? (
          <Button
            key={link.label}
            onClick={(e) => handleMenuOpen(e, link.submenu)}
            sx={{
              color: isTop ? "rgba(255, 255, 255, 1)" : "rgba(33, 93, 213, 1)",
              mx: 0,
              fontSize: 16,
              fontWeight: "bold",
            }}
          >
            {link.label}
          </Button>
        ) : (
          <Button
            key={link.label}
            component={Link}
            to={link.page}
            sx={{
              color: isTop ? "rgba(255, 255, 255, 1)" : "rgba(33, 93, 213, 1)",
              mx: 0,
              fontSize: 16,
              fontWeight: "bold",
            }}
          >
            {link.label}
          </Button>
        )
      )}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {submenuItems.map((item) => (
          <MenuItem
            key={item.label}
            onClick={handleMenuClose}
            component={Link}
            to={item.id}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: isTop
            ? "rgba(255, 255, 255, 0)"
            : "rgba(255, 255, 255, 1)",

          backdropFilter: isTop ? "blur(0px)" : "blur(10px)",
          WebkitBackdropFilter: isTop ? "blur(0px)" : "blur(10px)",
          // transition: "background-color 0.3s ease, backdrop-filter 0.3s ease",
          py: { xs: 1, sm: 1.2, md: 0 },
          px: { xs: 0, sm: 4, md: 2 },
          boxShadow: isTop ? "none" : "0 2px 8px rgba(0,0,0,0.05)",
          zIndex: (theme) => theme.zIndex.drawer,
          transition: "all 0.5s ease",
        }}
      >
        <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
          {/* Left: Logo + Company Name */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box
              component="img"
              src="/logo.png"
              alt="Logo"
              sx={{
                height: { xs: 40, sm: 50, md: 60, lg: 70 },
                mr: 1, // equivalent to marginRight: 10px
                width: "auto",
                objectFit: "contain",
              }}
            />
            <Typography
              component="div"
              sx={{
                flexGrow: 1,
                color: isTop
                  ? "rgba(255, 255, 255, 1)"
                  : "rgba(33, 93, 213, 1)",
                fontSize: { xs: 20, sm: 22, md: 24, lg: 26 },
                fontWeight: "bold",
              }}
            >
              Flyhi Trans Inc.
            </Typography>
          </Box>

          {/* Desktop Menu */}
          {!isMobile && (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {renderNavLinks()}
              {/* Social Icons */}
              {socialLinks.map((item) => (
                <IconButton
                  key={item.label}
                  component="a"
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: isTop
                      ? "rgba(255, 255, 255, 1)"
                      : "rgba(33, 93, 213, 1)",
                  }}
                >
                  {item.icon}
                </IconButton>
              ))}
            </Box>
          )}

          {/* Mobile Hamburger Menu */}
          {isMobile && (
            <IconButton
              edge="end"
              aria-label="menu"
              onClick={() => setDrawerOpen(true)}
            >
              <MenuIcon
                sx={{
                  color: isTop
                    ? "rgba(255, 255, 255, 1)"
                    : "rgba(33, 93, 213, 1)",
                }}
              />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      {/* Mobile Full-Screen Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(255, 255, 255, 1)",
          },
        }}
      >
        <Box
          sx={{
            p: 2,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header: Logo + Name + Close */}
          <Box
            sx={{
              position: "relative",
              mb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {/* Logo and Company Name */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <img
                src="/logo.png"
                alt="Logo"
                style={{ height: 40, marginRight: 10 }}
              />
              <Typography
                variant="h6"
                component="div"
                sx={{
                  color: isTop
                    ? "rgba(255, 255, 255, 1)"
                    : "rgba(33, 93, 213, 1)",
                  fontWeight: 700,
                }}
              >
                Flyhi Trans Inc.
              </Typography>
            </Box>

            <IconButton
              edge="end"
              aria-label="menu"
              onClick={() => setDrawerOpen(false)}
            >
              <CloseIcon sx={{ color: "rgba(33, 93, 213, 1)" }} />
            </IconButton>
          </Box>

          {/* Navigation Links */}
          <List sx={{ flexGrow: 1 }}>
            {navLinks.map((link) => (
              <React.Fragment key={link.label}>
                {link.submenu ? (
                  <>
                    <ListItem>
                      <ListItemText
                        primary={link.label}
                        primaryTypographyProps={{
                          fontWeight: "bolder",
                          color: "rgba(33, 93, 213, 1)",
                        }}
                      />
                    </ListItem>
                    {link.submenu.map((sublink) => (
                      <ListItem
                        key={sublink.label}
                        button
                        component={Link}
                        to={sublink.id} // use React Router path here
                        onClick={() => setDrawerOpen(false)}
                        sx={{ pl: 4 }}
                      >
                        <ListItemText primary={sublink.label} />
                      </ListItem>
                    ))}
                  </>
                ) : (
                  <ListItem
                    button
                    component={Link}
                    to={link.page} // use React Router path here
                    onClick={() => setDrawerOpen(false)}
                  >
                    <ListItemText
                      primary={link.label}
                      primaryTypographyProps={{
                        color: "rgba(33, 93, 213, 1)",
                      }}
                    />
                  </ListItem>
                )}
              </React.Fragment>
            ))}
          </List>

          {/* Social Icons at Bottom */}
          <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
            {socialLinks.map((item) => (
              <IconButton
                key={item.label}
                component="a"
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: "rgba(33, 93, 213, 1)" }}
              >
                {item.icon}
              </IconButton>
            ))}
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default Navbar;
